import React, { useEffect, useRef, useState } from 'react';
import { Message, OrderStatus } from '../types';
import { useFileUpload } from '../hooks/useFileUpload';
import { useSafeContent } from '../utils/sanitizer';
import { FilePreviewModal } from '../../../shared/components/FilePreviewModal';
import { isImageFile, isPdfFile, canPreviewFile } from '../../../shared/utils/fileUtils';
import { http } from '../../../shared/api/http';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import 'yet-another-react-lightbox/styles.css';


interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  orderStatus?: OrderStatus;
  className?: string;
  // Пагинация
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  // ID заказа для работы с файлами
  orderId?: string;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  orderStatus,
  className = '',
  hasMoreMessages = false,
  isLoadingMore = false,
  onLoadMore,
  orderId
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { formatFileSize } = useFileUpload();
  const { sanitizeMessage } = useSafeContent();

  // Состояние предпросмотра файла
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');
  
  // Состояние для Lightbox (просмотр изображений)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{src: string, alt: string} | null>(null);

  // Подбор иконки Ionicons по MIME или типу файла
  const getIonIconName = (file: any): string => {
    // Используем mime_type если есть, иначе type, иначе определяем по имени
    const mime = file.mime_type || file.type || '';
    const fileName = file.name || '';
    
    // Если MIME тип не определен, пробуем по расширению файла
    if (!mime && fileName.includes('.')) {
      const ext = fileName.toLowerCase().split('.').pop();
      const extToMime: Record<string, string> = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'pdf': 'application/pdf',
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed'
      };
      const detectedMime = extToMime[ext || ''] || '';
      if (detectedMime.startsWith('image/')) return 'image-outline';
      if (detectedMime === 'application/pdf') return 'document-outline';
      if (detectedMime.includes('zip') || detectedMime.includes('rar')) return 'archive-outline';
    }
    
    if (mime.startsWith('image/')) return 'image-outline';
    if (mime === 'application/pdf') return 'document-outline';
    if (mime.startsWith('video/')) return 'videocam-outline';
    if (mime.startsWith('audio/')) return 'musical-notes-outline';
    if (mime.includes('zip') || mime.includes('rar')) return 'archive-outline';
    if (mime.includes('word')) return 'document-text-outline';
    if (mime.includes('excel') || mime.includes('spreadsheet')) return 'grid-outline';
    if (mime.includes('text')) return 'document-text-outline';
    return 'attach-outline';
  };
  
  // Состояние для контроля автопрокрутки
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [userIsScrolling, setUserIsScrolling] = useState(false);
  const previousMessagesLength = useRef(messages.length);

  // Умная автопрокрутка - при первой загрузке и при добавлении новых сообщений
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Проверяем, добавились ли новые сообщения или это первая загрузка
    const hasNewMessages = messages.length > previousMessagesLength.current;
    const isFirstLoad = previousMessagesLength.current === 0 && messages.length > 0;
    previousMessagesLength.current = messages.length;

    // При первой загрузке всегда прокручиваем к концу
    if (isFirstLoad) {
      // Несколько попыток прокрутки для надежности
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 100);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 300);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 500);
      return;
    }

    // При добавлении новых сообщений прокручиваем только если пользователь внизу
    if (hasNewMessages && !userIsScrolling && shouldAutoScroll) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px буфер

      if (isNearBottom) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [messages, shouldAutoScroll, userIsScrolling]);

  // Отслеживание ручного скролла пользователя
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout: number;

    const handleScroll = () => {
      setUserIsScrolling(true);
      
      // Сбрасываем флаг через 1 секунду после окончания скролла
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setUserIsScrolling(false);
      }, 1000);

      // Проверяем, находится ли пользователь внизу
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      setShouldAutoScroll(isAtBottom);
    };

    container.addEventListener('scroll', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  /**
   * Форматирование времени сообщения
   */
  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ru-RU', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  /**
   * Получение иконки статуса сообщения
   */
  const getMessageStatusIcon = (status: string) => {
    switch (status) {
      case 'sending': return '⏳';
      case 'sent': return '✓';
      case 'delivered': return '✓✓';
      case 'read': return '✓✓';
      case 'error': return '❌';
      default: return '';
    }
  };

  /**
   * Очистка префиксов из сообщений
   */
  const cleanMessageContent = (content: string): string => {
    if (!content) return content;
    
    // Убираем префиксы "Сообщение технологу:", "Сообщение менеджеру:"
    const prefixes = [
      'Сообщение технологу:',
      'Сообщение менеджеру:',
      'Сообщение технологу',
      'Сообщение менеджеру'
    ];
    
    let cleanContent = content.trim();
    
    for (const prefix of prefixes) {
      if (cleanContent.startsWith(prefix)) {
        cleanContent = cleanContent.substring(prefix.length).trim();
        break;
      }
    }
    
    return cleanContent;
  };

  /**
   * Получение информации о системном сообщении
   */
  const getSystemMessageInfo = (message: Message) => {
    if (message.is_rejection) {
      return { icon: '❌', className: 'system rejection' };
    }
    if (message.is_calc_cancelled) {
      return { icon: '💡', className: 'system calc-cancelled' };
    }
    if (message.is_completion_notification) {
      return { icon: '✅', className: 'system completion-notification' };
    }
    if (message.is_defect_notification || message.is_order_defect) {
      return { icon: '🚫', className: 'system defect-notification' };
    }
    if (message.is_manager_rejection) {
      return { icon: '❌', className: 'system manager-rejection' };
    }
    if (message.is_revision_request) {
      return { icon: '🔄', className: 'system revision-request' };
    }
    if (message.is_contractor_price) {
      return { icon: '💰', className: 'system contractor-price' };
    }
    if (message.is_contractor_accepted) {
      return { icon: '🤝', className: 'system contractor-accepted' };
    }
    if (message.is_status_change) {
      return { icon: '📋', className: 'system status-change' };
    }
    if (message.is_deadline_notification) {
      return { icon: '⏰', className: 'system deadline-notification' };
    }
    if (message.message_type === 'confirmation') {
      return { icon: '⏰', className: 'system confirmation' };
    }
    return { icon: 'ℹ️', className: 'system' };
  };

  /**
   * Обработка клика по файлу
   */
  const handleFileClick = async (file: any, message: Message) => {
    console.log('🔥 КЛИК ПО ФАЙЛУ:', file);
    console.log('🔥 MIME TYPE:', file.mime_type, 'TYPE:', file.type, 'NAME:', file.name);
    try {
      // ВСЕГДА используем наш API для получения файлов (даже если есть URL из Bitrix)
      if (file.name) {
        // Получаем order_id из пропсов, URL или глобального состояния
        const currentOrderId = orderId || 
                              window.location.pathname.match(/\/orders\/(\d+)/)?.[1] || 
                              (window as any).currentOrderId;
        
        if (currentOrderId) {
          // ИСПРАВЛЕНИЕ: Создаем авторизованный URL для стриминга файла
          const createAuthorizedUrl = (forImage = false) => {
            // ИСПРАВЛЕНИЕ: Для изображений НЕ используем baseURL (который содержит /api/)
            // Для PDF и других файлов используем baseURL как раньше
            const baseUrl = forImage ? window.location.origin : (http.defaults.baseURL || window.location.origin);
            const streamPath = `/chats/file/${encodeURIComponent(currentOrderId)}/${encodeURIComponent(file.name)}`;
            
            // 🔥 ИСПРАВЛЕНИЕ: Получаем валидный токен
            const getValidToken = (): string | null => {
              const tokens = [
                localStorage.getItem('alfastil_token'),
                localStorage.getItem('alfastil_web_token')
              ]
              
              for (const token of tokens) {
                if (!token) continue
                try {
                  const payload = JSON.parse(atob(token.split('.')[1]))
                  const now = Math.floor(Date.now() / 1000)
                  if (payload.exp && payload.exp >= now) {
                    return token
                  }
                } catch {
                  continue
                }
              }
              return null
            }
            
            const token = getValidToken()
            
            if (token) {
              // Добавляем токен как query параметр для прямых запросов (img, iframe, etc.)
              const separator = streamPath.includes('?') ? '&' : '?';
              return `${baseUrl}${streamPath}${separator}auth_token=${encodeURIComponent(token)}`;
            } else {
              // Fallback без токена
              return `${baseUrl}${streamPath}`;
            }
          };
          
          // Определяем тип файла и создаем URL
          const fileType = file.mime_type || file.type;
          const fileUrl = createAuthorizedUrl(true); // Все файлы через /chats/file/
          
          // Открываем модальное окно с URL (лоадер будет показан в FilePreviewModal)
          setPreviewUrl(fileUrl);
          setPreviewName(file.name || 'file');
          setIsPreviewOpen(true);
        } else {
          console.warn('⚠️ Не удалось определить ID заказа для получения файла');
        }
      }
    } catch (error) {
      console.error('❌ Ошибка при открытии файла:', error);
    }
  };

  if (isLoading && messages.length === 0) {
    return (
      <div className={`chat-widget-loading ${className}`}>
        <div className="loading-spinner"></div>
        <span>Загрузка сообщений...</span>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className={`chat-widget-no-messages ${className}`}>
        <div className="no-messages-icon">💬</div>
        <p>Начните общение</p>
        <small>
          {orderStatus === 'in_review' 
            ? 'Задайте вопросы по заказу менеджеру или технологу'
            : 'Напишите первое сообщение'
          }
        </small>
      </div>
    );
  }

  return (
    <div className={`chat-widget-messages-container ${className}`}>
      <div className="chat-widget-messages" ref={messagesContainerRef}>
      
      {/* Кнопка "Загрузить еще" - компактная в стиле виджета */}
      {hasMoreMessages && messages.length > 0 && (
        <div className="flex justify-center py-2 px-4">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 py-1 px-2 rounded border border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            {isLoadingMore ? (
              <>
                <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Загрузка...</span>
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                <span>Загрузить еще</span>
              </>
            )}
          </button>
        </div>
      )}
      
      {messages.map((message, index) => {
        const isOwn = message.is_own;
        
        // Отладочная информация
        if (import.meta.env.DEV) {
          console.log(`💬 Сообщение ${message.id}:`, {
            author: message.author,
            is_own: message.is_own,
            recipient: message.recipient,
            content: message.content.substring(0, 50) + '...'
          });
        }
        
        const showDateSeparator = index === 0 || 
          new Date(message.created_at).toDateString() !== new Date(messages[index - 1].created_at).toDateString();

        return (
          <div key={message.id}>
            {/* Разделитель дат */}
            {showDateSeparator && (
              <div className="flex justify-center my-4">
                <span className="bg-white px-3 py-1 rounded-full text-xs text-gray-500 border">
                  {new Date(message.created_at).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: new Date(message.created_at).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                  })}
                </span>
              </div>
            )}

            {/* Сообщение */}
            <div 
              className={`chat-widget-message ${
                message.is_system 
                  ? getSystemMessageInfo(message).className 
                  : (isOwn ? 'own' : 'other')
              }`}
            >
              <div className={`message-content ${message.is_system ? 'system-message' : ''}`}>
                
                {message.is_system ? (
                  // Системное сообщение
                  <>
                    <div className="system-icon">
                      {getSystemMessageInfo(message).icon}
                    </div>
                    <div className="system-text">
                      <p>{sanitizeMessage(cleanMessageContent(message.content))}</p>
                    </div>
                    <div className="message-footer">
                      <span className="message-time">
                        {formatMessageTime(message.created_at)}
                      </span>
                    </div>
                  </>
                ) : (
                  // Обычное сообщение
                  <>
                    {/* Получатель сообщения (для исходящих) */}
                    {isOwn && message.recipient && (
                      <div className="message-recipient">
                        → {message.recipient === 'manager' ? 'Менеджер' : 'Технолог'}
                      </div>
                    )}


                      
                    {/* Текст сообщения */}
                    {message.content && (
                      <p>
                        {sanitizeMessage(cleanMessageContent(message.content))}
                      </p>
                    )}

                    {/* Время сообщения внутри рамки */}
                    <div className="message-time-inline">
                      {formatMessageTime(message.created_at)}
                      {isOwn && (
                        <span className="message-status">
                          {getMessageStatusIcon(message.status)}
                        </span>
                      )}
                    </div>
                  </>
                )}

                  {/* Файлы */}
                  {message.files && message.files.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message.files.map((file, fileIndex) => (
                        <div
                          key={fileIndex}
                          onClick={() => handleFileClick(file, message)}
                          className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                            isOwn 
                              ? 'bg-blue-700 hover:bg-blue-800' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-lg flex items-center justify-center">
                            <ion-icon name={getIonIconName(file)} style={{ fontSize: 18 }}></ion-icon>
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${
                              isOwn ? 'text-white' : 'text-gray-800'
                            }`}>
                              {file.name}
                            </p>
                            <p className={`text-xs ${
                              isOwn ? 'text-blue-200' : 'text-gray-500'
                            }`}>
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                          {canPreviewFile(file.mime_type || file.type, file.name) && (
                            <svg className={`w-4 h-4 ${isOwn ? 'text-blue-200' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>


              </div>
            </div>
        );
      })}

        {/* Индикатор загрузки */}
        {isLoading && messages.length > 0 && (
          <div className="flex justify-center py-2">
            <div className="loading-spinner"></div>
          </div>
        )}

        {/* Элемент для автопрокрутки */}
        <div ref={messagesEndRef} />
      </div>

      {/* Кнопка "К последнему сообщению" */}
      {!shouldAutoScroll && (
        <button
          className="scroll-to-bottom-btn"
          onClick={() => {
            setShouldAutoScroll(true);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          title="К последнему сообщению"
        >
          ↓
        </button>
      )}
      {/* Модальное окно предпросмотра */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fileUrl={previewUrl}
        fileName={previewName}
        onDownload={() => {
          const link = document.createElement('a');
          link.href = previewUrl;
          link.download = previewName;
          link.click();
        }}
      />

      {/* Lightbox для просмотра изображений */}
      <Lightbox
        open={lightboxOpen}
        close={() => {
          setLightboxOpen(false);
          setLightboxImage(null);
        }}
        plugins={[Zoom, Fullscreen]}
        slides={lightboxImage ? [lightboxImage] : []}
        styles={{
          container: { 
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 100, // Модальные окна должны быть на уровне 100
          },
        }}
      />
    </div>
  );
};
